import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

// Recurrence presets. The RRULE is DATE-ANCHORED: it repeats off whatever
// first-occurrence date the user picks (DTSTART = starts_at), so there is
// deliberately NO BYMONTHDAY / BYDAY clause — "Monthly" on the 15th recurs
// on the 15th, "Weekly" on a Wednesday recurs on Wednesdays, etc.
export const RECUR_OPTIONS: { value: string; label: string; rrule: string | null }[] = [
  { value: 'none',      label: 'Does not repeat', rrule: null },
  { value: 'weekly',    label: 'Weekly',          rrule: 'FREQ=WEEKLY' },
  { value: 'biweekly',  label: 'Every 2 weeks',   rrule: 'FREQ=WEEKLY;INTERVAL=2' },
  { value: 'monthly',   label: 'Monthly',         rrule: 'FREQ=MONTHLY' },
  { value: 'quarterly', label: 'Quarterly',       rrule: 'FREQ=MONTHLY;INTERVAL=3' },
  { value: 'yearly',    label: 'Yearly',          rrule: 'FREQ=YEARLY' },
];

// Friendly label for a stored rrule (falls back to the raw string).
export function labelForRrule(rrule: string): string {
  return RECUR_OPTIONS.find((o) => o.rrule === rrule)?.label ?? rrule;
}

export interface RecurringExpenseTemplate {
  id: number;
  name: string;
  project_id: number | null;
  type: string;
  amount: string;
  tax_amount: string;
  vendor_id: number | null;
  vendor: string | null;
  tax_category: string | null;
  reimbursable: boolean;
  notes: string | null;
  rrule: string;
  starts_at: number;
  ends_at: number | null;
  next_due_at: number | null;
  is_active: boolean;
  created_at: number;
  updated_at: number;
  project_case_number?: string | null;
  project_title?: string | null;
  created_by_name?: string | null;
}

export function useRecurringExpenses() {
  return useQuery({
    queryKey: ['recurring-expenses'],
    queryFn: ({ signal }) =>
      api.get<{ recurring_expense_templates: RecurringExpenseTemplate[] }>(
        '/api/recurring-expenses', signal),
  });
}

export interface CreateRecurringExpenseInput {
  name: string;
  project_id?: number | null;
  type: string;
  amount: string;
  tax_amount?: string;
  vendor_id?: number | null;
  /** Name-only vendor (a `generic_vendor` category or a one-off) for templates
   *  with no vendor record behind them. A `vendor_id` overrides it server-side,
   *  which re-snapshots the real name on every generated occurrence. */
  vendor?: string;
  tax_category?: string | null;
  reimbursable?: boolean;
  notes?: string;
  rrule: string;
  starts_at: number;
  ends_at?: number | null;
  is_active?: boolean;
  // Begin generating from the occurrence AFTER starts_at (record-now-plus-repeat).
  skip_first?: boolean;
}

export function useCreateRecurringExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRecurringExpenseInput) =>
      api.post<{ recurring_expense_template: RecurringExpenseTemplate; generated_count?: number }>(
        '/api/recurring-expenses', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recurring-expenses'] });
      // Create may backfill past-due expense rows immediately.
      void qc.invalidateQueries({ queryKey: ['expenses'] });
    },
  });
}

export interface UpdateRecurringExpenseInput {
  id: number;
  is_active?: boolean;
  name?: string;
  rrule?: string;
  amount?: string;
  tax_amount?: string;
  reimbursable?: boolean;
  notes?: string | null;
  tax_category?: string | null;
  starts_at?: number;
  ends_at?: number | null;
}

export function useUpdateRecurringExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateRecurringExpenseInput) =>
      api.patch<{ recurring_expense_template: RecurringExpenseTemplate }>(
        `/api/recurring-expenses/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recurring-expenses'] });
    },
  });
}

export function useDeleteRecurringExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/recurring-expenses/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['recurring-expenses'] });
    },
  });
}
